import { Tabs, Card, Progress, Button, Avatar, Row, Col, Tooltip } from "antd";
import "./Rewards.scss";
import { useCallback, useMemo, useState } from "react";
import { getApiRequest } from "../../services/getApiRequest";
import { showToast } from "../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import { END_POINTS } from "../../Helper/Constant";
import { useTranslation } from "react-i18next";
import { postApiRequest } from "../../services/postApiRequest";
import { useDispatch, useSelector } from "react-redux";
import { hideLoader, showLoader } from "../../features/loader/loaderSlice";
import { useNavigate } from "react-router-dom";
import { setUser } from "../../features/user/userSlice";
import { setLocalStorageData } from "../../shared/commonFunction";
import coinsIcon from "../../assets/icons/header-coin.svg";
import { fetchGamificationProgress } from "../../features/gamification/gamificationSlice";
import Swal from "sweetalert2";
import coinIcon from "../../assets/icons/header-coin.svg";

/* ---------------- ICON MAPPING ---------------- */
const TASK_ICON_MAP = {
  REAL_GAMER: "/images/reward1.png",
  TOURNAMENTS_MASTER: "/images/reward2.png",
};

const Rewards = () => {
  // const [taskList, setTaskList] = useState([]);
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tasks: taskList, loading, error } = useSelector(
    (state) => state.gamification
  );

  // USER UPDATE API functions
  const fetchUserData = useCallback(async () => {
    try {
      const response = await getApiRequest(END_POINTS.GET_USER_DATA);
      if (response.success) {
        dispatch(setUser(response.user));
        setLocalStorageData("user", JSON.stringify(response.user));
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || error?.error?.message,
        "error",
      );
    }
  }, [dispatch]);

  // Mobile APP Buttons

  const MobileAppButton = () => (
    <div className="mobile-only-wrapper">
      <Button className="claim mobile-only" disabled>
        {t("reward.available_mobile_app")}
      </Button>

      <div className="store-links">
        <a
          href="https://play.google.com/store/apps/details?id=com.fanzaty.app"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
            alt="Google Play"
          />
        </a>

        <a
          href="https://apps.apple.com/in/app/fanzaty/id6746043884"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
            alt="App Store"
          />
        </a>
      </div>
    </div>
  );
  /* ---------------- REDIRECT TO TASK ---------------- */

  // redirect to task page based on task type
  const redirectToTask = (item) => {
    const { type } = item.task || {};
    if (type === "TOURNAMENT_PLAY" || type === "WIN_STREAK") {
      navigate("/all-tournaments");
      return;
    }
    if (type === "LEADERBOARD_RANKING") {
      navigate("/leaderboard");
      return;
    }
    if (type === "GIFTCARD_PURCHASE") {
      navigate("/store");
      return;
    }
    if (
      (type === "DAILY_MATCH_STREAK" || type == "DUEL_PLAY")
    ) {
      navigate("/one-to-one");
    }
  };
  /* ---------------- CLAIM REWARD ---------------- */
  const handleClaimReward = async (item) => {
    if (!item.isCompleted) {
      redirectToTask(item);
      return;
    }

    dispatch(showLoader());

    try {
      const response = await postApiRequest(END_POINTS.CLAIM_REWARDS, {
        progressId: item.id,
      });

      dispatch(hideLoader());

      if (response?.success) {

        const result = await Swal.fire({
          title: t("reward.reward_claimed"),
          text: response?.message || "Reward claimed successfully!",
          imageUrl: coinIcon,
          imageWidth: 70,
          imageHeight: 70,
          imageAlt: "Coins",
          confirmButtonText: t("reward.ok"),
          confirmButtonColor: "#08ff96",
          background: "#0f0f0f",
          color: "#ffffff",
          className: "reward-claimed-popup",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: (popup) => {
            popup.style.border = "2px solid #08ff96";
            popup.style.borderRadius = "14px";
          },
        });

        // 👇 IMPORTANT PART
        if (result.isConfirmed) {
          dispatch(showLoader());

          try {
            await fetchUserData();
            await dispatch(fetchGamificationProgress());
          } finally {
            dispatch(hideLoader());
          }
        }
      }
    } catch (error) {
      dispatch(hideLoader());

      Swal.fire({
        icon: "error",
        title: "Oops!",
        text:
          error?.response?.data?.message ||
          error?.error?.message ||
          error?.message,
        confirmButtonText: "OK",
        confirmButtonColor: "#ff4d4f",
        background: "#0f0f0f",
        color: "#ffffff",
      });
    }
  };



  /* ---------------- FORMAT DATA ---------------- */
  const formattedTasks = useMemo(() => {
    return taskList.map((item) => ({
      id: item._id,
      title: item.task.title,
      desc: item.task.description,
      progress: item.progress,
      total: item.task.target,
      coins: item.task.coinReward,
      claimed: item.isClaimed,
      isCompleted: item.isCompleted,
      icon: TASK_ICON_MAP[item.task.key] || "/images/default-reward.png",
      task: item.task,
    }));
  }, [taskList]);

  // const activeRewards = useMemo(
  //   () => formattedTasks.filter((task) => !task.claimed && task.isCompleted),
  //   [formattedTasks]
  // );

  const claimedRewards = useMemo(
    () => formattedTasks.filter((task) => task.claimed),
    [formattedTasks],
  );

  /* ---------------- RENDER LIST ---------------- */
  const renderRewards = (data, showProgress) => (
    <div className="reward-list">
      {data.map((item) => (
        <div className="reward-row" key={item.id}>
          <Row align="middle" gutter={[16, 16]}>
            {/* ICON + TITLE */}
            <Col xs={24} md={10} className="icon-col">
              <Avatar
                src={item?.task?.logo}
                size={56}
                className="reward-icon"
              />
              <div className="reward-title-desc">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </Col>

            {/* PROGRESS */}
            <Col xs={24} md={6} className="info-col">
              {showProgress && (
                <div className="progress-section">
                  <Progress
                    percent={(item.progress / item.total) * 100}
                    showInfo={false}
                    strokeColor="#08ff96"
                  />
                  <span className="count">
                    <span style={{ color: "#08ff96" }}>{item.progress}</span>/
                    {item.total}
                  </span>
                </div>
              )}
            </Col>

            {/* ACTION */}
            <Col xs={24} md={8} className="action-col">
              <div className="action-wrapper">

                <div className="coin-box">
                  <Tooltip title={t("reward.coins")}>
                    <div className="coin-content">
                      <img src={coinsIcon} alt="coin-icon" className="market-place-coin" />
                      <span className="coin-value">{item.coins}</span>
                      <span className="coin-label">{t("reward.coins")}</span>
                    </div>
                  </Tooltip>
                </div>

                {["TEAM_FANZATY", "REFERRAL"].includes(item.task?.type) ? (
                  <MobileAppButton />
                ) : item.claimed ? (
                  <Button disabled className="claimed">
                    {t("reward.claimed_rewards")}
                  </Button>
                ) : (
                  <Button
                    className="claim"
                    onClick={() => handleClaimReward(item)}
                  >
                    {t("reward.Claim_Prize")}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );

  return (
    <section className="rewards-section">
      <div className="container">
        <h2 className="page-title">{t("reward.heading")}</h2>

        <Card className="rewards-box">
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: "1",
                label: t("reward.heading"),
                children: renderRewards(formattedTasks, true),
              },
              {
                key: "2",
                label: t("reward.claimed_rewards"),
                children: renderRewards(claimedRewards, false),
              },
            ]}
          />
        </Card>
      </div>
    </section>
  );
};

export default Rewards;
